import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { companyIdSelector, isGTSelector } from '../store/Slices/companySlice';
import { useRqChildCompanies } from '../react_query/messages/sharing/useRqChildCompanies';
import { useRqVisibleStations } from '../react_query/stations/useRqVisibleStations';
import { useUserCanAny } from './useUserCan';

export type ISharedFacilityMusicAccessMode = 'district' | 'child' | null;

export const useSharedFacilityMusicAccess = () => {
  const companyId = useSelector(companyIdSelector);
  const isGT = useSelector(isGTSelector);
  const canViewSharedFacility = useUserCanAny(
    'is-admin,partner,district-facility-sharing:view',
  );
  const canViewVisibleStations = useUserCanAny(
    'is-admin,partner,district-hierarchy:view',
  );
  const canViewDistrictHierarchy = useUserCanAny('is-admin,district-hierarchy:view');

  const {data: visibleStations, isLoading: isLoadingVisibleStations} =
    useRqVisibleStations(
      canViewSharedFacility && canViewVisibleStations ? companyId : 0,
    );
  const {data: childCompanies, isLoading: isLoadingChildCompanies} =
    useRqChildCompanies(
      companyId,
      Boolean(
        canViewSharedFacility &&
          canViewDistrictHierarchy &&
          isGT &&
          companyId,
      ),
    );

  const hasInheritedSharedFacility = useMemo(() => {
    return (visibleStations || []).some(station => {
      return Boolean(
        station.is_inherited &&
          station.is_shared_facility &&
          station.visibility_reason === 'district_shared_facility',
      );
    });
  }, [visibleStations]);

  const hasActiveChildCompanies = useMemo(() => {
    return (childCompanies || []).some(company => company.is_active);
  }, [childCompanies]);

  const accessMode: ISharedFacilityMusicAccessMode = useMemo(() => {
    if (hasInheritedSharedFacility) {
      return 'child';
    }

    if (canViewSharedFacility && canViewDistrictHierarchy && isGT && hasActiveChildCompanies) {
      return 'district';
    }

    return null;
  }, [
    canViewDistrictHierarchy,
    canViewSharedFacility,
    hasActiveChildCompanies,
    hasInheritedSharedFacility,
    isGT,
  ]);

  return {
    accessMode,
    isEligible: Boolean(accessMode),
    isLoading: Boolean(
      canViewSharedFacility &&
        canViewVisibleStations &&
        (isLoadingVisibleStations ||
          (canViewDistrictHierarchy && isGT && isLoadingChildCompanies)),
    ),
    hasActiveChildCompanies,
    hasInheritedSharedFacility,
  };
};
